'use client';

import type { PropsWithChildren } from 'react';
import React, { useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  Prominence,
  Sizes,
  Text,
  Variants,
} from '@sola/ark-core/atoms/text/text.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { Checkbox } from '@sola/ark-core/molecules/checkbox/checkbox.tsx';
import { DateField } from '@sola/ark-core/molecules/date-field/date-field.tsx';
import { EmailField } from '@sola/ark-core/molecules/email-field/email-field.tsx';
import { Fieldset } from '@sola/ark-core/molecules/fieldset/fieldset.tsx';
import { Form, FormSubmit } from '@sola/ark-core/molecules/form/form.tsx';
import { InputField } from '@sola/ark-core/molecules/input-field/input-field.tsx';
import { PhoneField } from '@sola/ark-core/molecules/phone-field/phone-field.tsx';
import { PostalCodeField } from '@sola/ark-core/molecules/postal-code-field/postal-code-field.tsx';
import type { SelectItemProps } from '@sola/ark-core/molecules/select/select.tsx';
import { Select } from '@sola/ark-core/molecules/select/select.tsx';
import { StateField } from '@sola/ark-core/molecules/state-field/state-field.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { generalConfig } from '@sola/config/lib/getters.ts';
import type { CountryCode } from '@sola/schema/dotcom/shared/geo.ts';
import { StateEnum } from '@sola/schema/dotcom/shared/geo.ts';
import type { FormDynamicSettings } from '@sola/schema/lib/schema.ts';
import type { LeadEventRequest } from '@sola/schema/lib/skippy.ts';
import { trackFormSuccess } from '@sola/tracking/components/datalayer.tsx';
import { camelCaseToLabel } from '@sola/utils/string.ts';

import { NameFields } from '../../../molecules/name-fields/name-fields.tsx';
import { AssetLinksFooter } from '../asset-links.tsx';
import { parseInlineLinks } from '../parse-inline-links.tsx';

export type EventFormProps = {
  onSubmit: (data: LeadEventRequest) => Promise<boolean>;
  config?: Partial<FormDynamicSettings>;
};

const EventFormState = z.object({
  isSubmitting: z.boolean().optional(),
  errorMessage: z.string().optional(),
  showThankYou: z.boolean().optional(),
});
type EventFormState = z.infer<typeof EventFormState>;

const FormQueryState = z.object({
  isPastor: z.boolean().optional(),
  isChildHost: z.boolean().optional(),
  hasPermissionToHost: z.boolean().optional(),
  acknowledged: z.boolean().optional(),
  consent: z.boolean().optional(),
});
type FormQueryState = z.infer<typeof FormQueryState>;

const AddressFieldsProps = z.object({
  fieldGroupPrefix: z.string().optional(),
  autoCompletePrefix: z.string().optional(),
});
type AddressFieldsProps = z.infer<typeof AddressFieldsProps>;

const FormColumnProps = z.object({
  className: z.string().optional(),
});
type FormColumnProps = z.infer<typeof FormColumnProps> & PropsWithChildren;

type MaterialItemsProps = {
  config?: Partial<FormDynamicSettings>;
};

type ItemProps = {
  id?: string;
  pmId?: string;
  kitId?: string;
  label?: string;
};

export const stateOptions = Object.entries(StateEnum).map(([abbr, name]) => ({
  text: name,
  value: abbr,
}));

const makeOption = (value: string) => {
  return { text: camelCaseToLabel(value), value };
};

export const FormColumn: React.FC<FormColumnProps> = ({
  className,
  children,
}) => (
  <div
    className={cn('grid sm:grid-cols-2 gap-comfy-sm', {
      [`${className}`]: !!className,
    })}
  >
    {children}
  </div>
);

const MaterialItems: React.FC<MaterialItemsProps> = ({ config }) => {
  const { t } = useTranslation(['forms']);
  const orderGroups = config?.materialOrder;

  const getItems = (options: ItemProps[]): SelectItemProps[] => {
    return options.map((option) => ({
      value: option.pmId ?? option.id ?? '',
      text: option.label ?? '',
    }));
  };

  if (!orderGroups || orderGroups.length === 0) {
    return null;
  }

  return (
    <Fieldset
      header={config?.materialOrderHeading ?? t('forms:materialOrder.header')}
    >
      {orderGroups?.map((group, index) => {
        return (
          <Select
            key={`items-${index}`}
            label={t(group.label ?? `Group ${index}`)}
            name={`items-${index}`}
            items={getItems(group.options ?? [])}
            placeholder={t('forms:materialOrder.placeholder')}
          />
        );
      })}
    </Fieldset>
  );
};

const AddressFields: React.FC<AddressFieldsProps> = ({
  fieldGroupPrefix,
  autoCompletePrefix,
}) => {
  const { t } = useTranslation(['core', 'forms', 'skippy']);

  const getTranslationKey = (key: string): string => {
    return `forms:addressFields.${key}`;
  };

  const isChurchFields = fieldGroupPrefix === 'church';
  const formattedAutoCompletePrefix = autoCompletePrefix
    ? `${autoCompletePrefix} `
    : '';

  return (
    <>
      <InputField
        label={
          isChurchFields
            ? t('skippy:form.compassionSunday.churchAddressLabel')
            : t(getTranslationKey('addressLabel'))
        }
        name={`${fieldGroupPrefix}Address`}
        autoComplete={`${formattedAutoCompletePrefix}address-line1`}
        required
      />
      <FormColumn>
        <InputField
          label={t(getTranslationKey('cityLabel'))}
          name={`${fieldGroupPrefix}City`}
          autoComplete={`${formattedAutoCompletePrefix}address-level2`}
          required
          maxLength={generalConfig.leadForms.churchCityMaxLength}
        />
        <StateField
          label={t(getTranslationKey('stateLabel'))}
          name={`${fieldGroupPrefix}State`}
          autoComplete={`${formattedAutoCompletePrefix}address-level1`}
          spellCheck={false}
          placeholder={t(getTranslationKey('statePlaceholder'))}
          items={stateOptions}
          required
        />
        <PostalCodeField
          label={t(getTranslationKey('zipCodeLabel'))}
          name={`${fieldGroupPrefix}Postal`}
          autoComplete={`${formattedAutoCompletePrefix}postal-code`}
          required
        />
      </FormColumn>
    </>
  );
};

export const EventForm: React.FC<EventFormProps> = ({ onSubmit, config }) => {
  const { t } = useTranslation(['forms', 'skippy']);
  const [formState, setFormState] = useState<EventFormState>({});
  const [
    { isPastor, isChildHost, hasPermissionToHost, acknowledged, consent },
    setQueryState,
  ] = useState<FormQueryState>({});
  const defaultCountry = 'US' as CountryCode;

  const audienceSizes: SelectItemProps[] = [
    { value: '', text: t('forms:event.audienceSize.placeholder') },
    ...[
      '1-49',
      '50-99',
      '100-199',
      '200-299',
      '300-399',
      '400-499',
      '500-749',
      '750-999',
      '1,000',
      '2,000',
      '3,000',
      '4,000',
      '5,000',
      '6,000',
      '7,000',
      '8,000',
      '9,000',
      '10,000+',
    ].map((value) => ({ value, text: value })),
  ];

  const roleOptions = [
    { text: t('skippy:form.roleOptions.defaultText'), value: '' },
    ...(config?.roles && config.roles.length > 0
      ? config.roles!
      : [
          t('skippy:form.roleOptions.pastor'),
          t('skippy:form.roleOptions.churchStaff'),
          t('skippy:form.roleOptions.congregant'),
        ]
    ).map(makeOption),
  ];

  const handleSubmit = async (data: FieldValues) => {
    let errorMessage = '';
    setFormState((prevState) => ({
      ...prevState,
      errorMessage,
      isSubmitting: true,
    }));

    const items = (config?.materialOrder ?? []).flatMap((_, index) => {
      return data[`items-${index}`] ?? [];
    });

    const transformedData: LeadEventRequest = {
      ...(data as LeadEventRequest),
      items,
    };

    try {
      const success = await onSubmit(transformedData as LeadEventRequest);

      if (success) {
        trackFormSuccess(config?.leadType ?? 'Event Form');
        setFormState({
          showThankYou: true,
          isSubmitting: false,
        });
        return;
      }

      errorMessage = t('skippy:form.errorVerify');
    } catch {
      errorMessage = t('skippy:form.errorSave');
    }

    if (errorMessage) {
      setFormState({
        isSubmitting: false,
        errorMessage,
      });
    }
  };

  if (formState.showThankYou) {
    const assets = config?.thankYouAssets;
    return (
      <Alert
        variant="positive"
        message={
          config?.messageThankYou ?? t('skippy:form.leadCreate.thankYou')
        }
        footer={
          assets && assets.length > 0 ? (
            <AssetLinksFooter assets={assets} />
          ) : undefined
        }
        scrollBehavior="instant"
        scrollPosition="center"
        scrollIntoView
      />
    );
  }

  return (
    <div className="EventForm max-w-sm">
      {formState.errorMessage && (
        <div className="mb-comfy-xs">
          <Alert
            className="scroll-mt-roomy-md"
            variant="negative"
            message={formState.errorMessage}
            scrollIntoView
          />
        </div>
      )}

      <Form onSubmit={handleSubmit}>
        <Fieldset
          id="contact-fields"
          header={t('forms:contactFields.contactHeader')}
        >
          <FormColumn>
            <NameFields />
          </FormColumn>
          <EmailField
            label={t('forms:contactFields.emailLabel')}
            name="email"
            required
          />
          {!config?.phone?.hide && (
            <PhoneField
              label={t('forms:contactFields.phoneNumberLabel')}
              name="phone"
              countryCode={defaultCountry}
              required={config?.phone?.required}
            />
          )}
          {!config?.role?.hide && (
            <Select
              label={t('forms:contactFields.roleLabel')}
              name="role"
              required={config?.role?.required}
              items={roleOptions}
            />
          )}
        </Fieldset>
        {config?.hasIdentifierInfo && (
          <Fieldset id="identifier-fields">
            <Text variant={Variants.Label} size={Sizes.Medium}>
              {t('forms:contactFields.identifierInfo')}
            </Text>
            <Checkbox
              label={t('forms:contactFields.isPastorLabel')}
              name="isPastor"
              onChange={({ currentTarget }) =>
                setQueryState((prevState) => ({
                  ...prevState,
                  isPastor: currentTarget.checked,
                }))
              }
            />
            <Checkbox
              label={t('forms:contactFields.isChildHost')}
              name="withKids"
              onChange={({ currentTarget }) =>
                setQueryState((prevState) => ({
                  ...prevState,
                  isChildHost: currentTarget.checked,
                }))
              }
            />
          </Fieldset>
        )}
        <Fieldset
          id="shipping-fields"
          header={t('forms:shippingFields.shippingHeader')}
        >
          <AddressFields
            fieldGroupPrefix="shipping"
            autoCompletePrefix="shipping"
          />
        </Fieldset>
        <MaterialItems config={config} />
        {config?.hasChurch && (
          <Fieldset id="event-fields" header={t('forms:event.eventHeader')}>
            <DateField
              label={t('forms:event.eventDateLabel')}
              name="eventDate"
              minDaysOut={1}
              shouldSetInitialDate={false}
              required
            />
            <Text
              variant={Variants.Label}
              size={Sizes.Medium}
              prominence={Prominence.Subtle}
              asChild
            >
              <p>{t('skippy:form.compassionSunday.churchInfoDisclaimer')}</p>
            </Text>
            <InputField
              label={t('forms:churchFields.churchNameLabel')}
              name="churchName"
              autoComplete="organization"
              required
            />
            <AddressFields fieldGroupPrefix="church" autoCompletePrefix="" />

            {!config?.website?.hide && (
              <InputField
                label={t('forms:churchFields.churchWebsiteLabel')}
                name="website"
                required={config?.website?.required}
                type="url"
              />
            )}

            <Select
              label={t('forms:event.audienceSize.label')}
              name="audienceSize"
              helpText={t('forms:event.audienceSize.helpText')}
              items={audienceSizes}
              required
            />
          </Fieldset>
        )}
        {(config?.consent ||
          (config?.hasIdentifierInfo && (!isPastor || isChildHost))) && (
          <Fieldset className={cn('mt-comfy-xl')}>
            {config?.hasIdentifierInfo && !isPastor && (
              <Checkbox
                label={t('skippy:form.compassionSunday.permissionLabel')}
                name="permission"
                required={!isPastor}
                onChange={({ currentTarget }) =>
                  setQueryState((prevState) => ({
                    ...prevState,
                    hasPermissionToHost: currentTarget.checked,
                  }))
                }
              />
            )}
            {config?.hasIdentifierInfo && isChildHost && (
              <Checkbox
                label={t('skippy:form.compassionSunday.acknowledgeLabel')}
                name="acknowledged"
                required={isChildHost}
                onChange={({ currentTarget }) =>
                  setQueryState((prevState) => ({
                    ...prevState,
                    acknowledged: currentTarget.checked,
                  }))
                }
              />
            )}
            {config?.consent && (
              <Checkbox
                name="consent"
                label={config.consentText || t('forms:consentText')}
                labelContent={
                  config.consentText
                    ? parseInlineLinks(config.consentText)
                    : undefined
                }
                required
                onChange={({ currentTarget }) =>
                  setQueryState((prevState) => ({
                    ...prevState,
                    consent: currentTarget.checked,
                  }))
                }
              />
            )}
          </Fieldset>
        )}

        <FormSubmit
          className="mt-comfy-xl"
          isBusy={formState.isSubmitting}
          disabled={
            (config?.hasIdentifierInfo && !isPastor && !hasPermissionToHost) ||
            (config?.hasIdentifierInfo && isChildHost && !acknowledged) ||
            (config?.consent && !consent)
          }
        >
          {config?.buttonText ?? t('forms:event.buttonText')}
        </FormSubmit>
      </Form>
    </div>
  );
};
